import { useState, useRef } from 'react'
import { Play, Square, ArrowRight, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { useToast } from '@/hooks/use-toast'
import { useBrand, splitBrandName } from '@/hooks/useBrand'
import api from '@/lib/api'
import type { VoiceOption } from './types'

interface BuilderPhaseVoiceProps {
  voiceOptions: VoiceOption[]
  selectedVoiceId: string
  tonePreset: 'warm' | 'balanced' | 'formal'
  agentName: string
  greeting: string
  voicesLoading: boolean
  isLastPhase?: boolean
  saving?: boolean
  onSelectVoice: (id: string) => void
  onSelectTone: (tone: 'warm' | 'balanced' | 'formal') => void
  onSetAgentName: (name: string) => void
  onSetGreeting: (greeting: string, manual?: boolean) => void
  onContinue: () => void
}

export default function BuilderPhaseVoice({
  selectedVoiceId,
  agentName,
  greeting,
  isLastPhase = false,
  saving = false,
  onSelectVoice,
  onSetAgentName,
  onSetGreeting,
  onContinue,
}: BuilderPhaseVoiceProps) {
  const { toast } = useToast()
  const brand = useBrand()
  const [playingId, setPlayingId] = useState<string | null>(null)
  const [loadingPreview, setLoadingPreview] = useState<string | null>(null)
  const [showGreetingEdit, setShowGreetingEdit] = useState(false)
  const audioRef = useRef<HTMLAudioElement | null>(null)

  // Telnyx resolves {{timeOfDay}} at call time from the firm's timezone.
  // We still show the variable in the UI (so James can see it's there) but
  // also tell them what it'll resolve to right now.
  const currentTimeOfDay = (() => {
    const hour = new Date().getHours()
    return hour < 12 ? 'morning' : hour < 17 ? 'afternoon' : 'evening'
  })()
  const hasTimeOfDay = /\{\{timeOfDay\}\}/.test(greeting)

  const handlePlay = async (voice: VoiceOption) => {
    if (playingId === voice.voice_id) {
      audioRef.current?.pause()
      audioRef.current = null
      setPlayingId(null)
      return
    }
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null }
    setLoadingPreview(voice.voice_id)
    try {
      const res = await api.get(`/api/voice/preview/${voice.voice_id}`, {
        responseType: 'blob',
        params: { text: greeting }
      })
      const url = URL.createObjectURL(res.data)
      const audio = new Audio(url)
      audioRef.current = audio
      setPlayingId(voice.voice_id)
      audio.play().catch(() => setPlayingId(null))
      audio.addEventListener('ended', () => { setPlayingId(null); audioRef.current = null; URL.revokeObjectURL(url) })
    } catch (err: any) {
      setPlayingId(null)
      const status = err?.response?.status
      toast({
        title: status === 503 ? 'Voice service unavailable' : 'Preview failed',
        description: err?.response?.data?.error || err?.response?.data?.message || 'Could not play voice preview. Try again.',
        variant: 'destructive',
      })
    } finally {
      setLoadingPreview(null)
    }
  }

  // Default voice — Molly (LegalLady's brand standard). Voice selection
  // itself is platform-admin only; org admins always land here on Molly.
  const defaultVoice: VoiceOption = { voice_id: 'molly', name: 'Molly', gender: 'female' }

  // Auto-select default voice on mount
  if (selectedVoiceId !== 'molly') {
    onSelectVoice('molly')
  }

  return (
    <div className="space-y-6">
      {/* Agent name */}
      <div className="space-y-2">
        <p className="text-sm font-medium">What should your AI go by?</p>
        <Input
          value={agentName}
          onChange={(e) => onSetAgentName(e.target.value)}
          placeholder="e.g. Sarah, Alex, Jamie"
          className="max-w-48"
        />
        {!agentName.trim() && (
          <p className="text-xs text-destructive">Agent name is required</p>
        )}
        <p className="text-xs text-muted-foreground">This is how the AI introduces itself on calls.</p>
      </div>

      {/* Voice */}
      <div className="space-y-3">
        <p className="text-sm font-medium">Your AI voice</p>
        <div className="flex items-center gap-3 p-3 rounded-lg border border-brand-royal bg-brand-royal/5 ring-1 ring-brand-royal">
          <div className="flex-1">
            <span className="text-sm font-medium">Molly · {splitBrandName(brand.appName)[0]}'s AI receptionist</span>
            <p className="text-xs text-muted-foreground">Warm, professional, and natural — our default brand voice</p>
          </div>
          <button
            className="h-8 w-8 rounded-full bg-muted flex items-center justify-center hover:bg-muted-foreground/20 transition-colors"
            onClick={() => handlePlay(defaultVoice)}
            disabled={loadingPreview === 'molly'}
          >
            {loadingPreview === 'molly' ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : playingId === 'molly' ? (
              <Square className="h-3.5 w-3.5" />
            ) : (
              <Play className="h-3.5 w-3.5 ml-0.5" />
            )}
          </button>
        </div>
      </div>

      {/* Greeting preview */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <p className="text-sm font-medium">Opening greeting</p>
          {!showGreetingEdit && (
            <button
              className="text-xs text-brand-royal hover:underline"
              onClick={() => setShowGreetingEdit(true)}
            >
              Customize
            </button>
          )}
        </div>
        {showGreetingEdit ? (
          <>
            <Textarea
              value={greeting}
              onChange={(e) => onSetGreeting(e.target.value, true)}
              className="text-sm min-h-[60px] resize-none"
            />
            <p className="text-xs text-muted-foreground">
              Use <code className="text-[11px] px-1 py-0.5 rounded bg-muted">{'{{timeOfDay}}'}</code> to have the greeting say "morning / afternoon / evening" automatically based on when the call comes in.
            </p>
          </>
        ) : (
          <div className="p-3 rounded-lg bg-muted/50 border">
            <p className="text-sm italic text-muted-foreground">"{greeting}"</p>
            {hasTimeOfDay && (
              <p className="text-[11px] text-muted-foreground/80 mt-1.5">
                <code className="text-[11px] px-1 py-0.5 rounded bg-muted not-italic">{'{{timeOfDay}}'}</code> auto-fills on each call — right now it would say "{currentTimeOfDay}".
              </p>
            )}
          </div>
        )}
      </div>

      <Button className="w-full" onClick={onContinue} disabled={!selectedVoiceId || !agentName.trim() || saving}>
        {saving ? (
          <>
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            {isLastPhase ? 'Saving...' : 'Continue'}
          </>
        ) : (
          <>
            {isLastPhase ? 'Save Changes' : 'Continue'}
            <ArrowRight className="h-4 w-4 ml-2" />
          </>
        )}
      </Button>
    </div>
  )
}
